import { useEffect } from "react";
import { isRouteErrorResponse, useLocation, useRouteError } from "react-router-dom";

import { useT } from "../i18n/index.js";
import { reportUiError } from "../shared/observability/sentry.js";
import { ROUTES } from "./routes.js";

/**
 * HQ-2026-009 — `errorElement` của router.
 *
 * Trước đây một trang ném lỗi lúc vẽ là màn hình trắng kèm câu mặc định tiếng Anh của react-router, và không ai
 * biết. React Router BẮT lỗi đó (nên `window.onerror` của Sentry không thấy) — vì vậy trang này tự báo.
 *
 * 404 của router (`isRouteErrorResponse`, status 404) là đường dẫn gõ sai, không phải lỗi của hub ⇒ không báo.
 *
 * Nút « Về trang đầu » là thẻ `<a>` (tải lại cả trang), không phải `<Link>`: cây React vừa hỏng, đi tiếp trong
 * chính cây đó dễ gặp lại đúng lỗi cũ.
 */
export function RouteErrorPage() {
  const error = useRouteError();
  const { pathname } = useLocation();
  const t = useT("common");
  const notFound = isRouteErrorResponse(error) && error.status === 404;

  useEffect(() => {
    if (!notFound) reportUiError(error, { route: pathname });
  }, [error, notFound, pathname]);

  const detail = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? `${error.name}: ${error.message}`
      : String(error);

  return (
    <main role="alert" className="mx-auto flex min-h-screen max-w-lg flex-col justify-center gap-4 p-6">
      <h1 className="text-xl font-semibold">{notFound ? t("routeError.notFoundTitle") : t("routeError.title")}</h1>
      <p className="text-muted-foreground">{notFound ? t("routeError.notFoundBody") : t("routeError.body")}</p>
      <div className="flex flex-wrap gap-2">
        {!notFound && (
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded bg-foreground px-3 py-2 text-background"
          >
            {t("routeError.reload")}
          </button>
        )}
        <a href={ROUTES.attention} className="rounded border px-3 py-2">
          {t("routeError.home")}
        </a>
      </div>
      {!notFound && (
        <details className="text-sm text-muted-foreground">
          <summary className="cursor-pointer">{t("routeError.detail")}</summary>
          <pre className="mt-2 whitespace-pre-wrap break-words">{detail}</pre>
        </details>
      )}
    </main>
  );
}
